import React, {
  AnchorHTMLAttributes,
  ButtonHTMLAttributes,
  MouseEvent,
  useMemo
} from 'react';
import styled, { css } from 'styled-components';
import { Link as LinkGatsby } from 'gatsby';
import {
  alignSelf,
  borderRadius,
  color,
  display,
  fontSize,
  fontWeight,
  flexShrink,
  justifySelf,
  gridArea,
  left,
  letterSpacing,
  lineHeight,
  maxWidth,
  position,
  space,
  textAlign,
  width
} from 'styled-system';

import { buttonReset, fontFamilySans, hoverDefault } from '../utils/styles';
import { openChat } from '../utils/utils';

const styles = css<{
  hover?: boolean;
}>`
  ${fontFamilySans};
  ${borderRadius};
  ${display};
  ${position};
  ${space};
  ${fontSize};
  ${fontWeight};
  ${flexShrink}
  ${lineHeight};
  ${width};
  ${maxWidth};
  ${alignSelf};
  ${justifySelf};
  ${textAlign};
  ${left};
  ${letterSpacing};
  ${gridArea};
  color: inherit;
  text-decoration: inherit;
  ${color};

  ${(props) =>
    props.hover &&
    css`
      ${hoverDefault};
    `};
`;

const config = {
  shouldForwardProp: (prop: any) =>
    ![
      'isHomePage',
      'altStyling',
      'semiTransparent',
      'whiteTransparent',
      'bigPadding',
      'borderColorPrimary',
      'borderPrimaryColorPrimary',
      'hover',
      'small',
      'active',
      'invalid',
      'transparent',
      'white',
      'colorPrimary'
    ].includes(prop)
};

const ButtonLink = styled.button.withConfig(config)`
  ${buttonReset};
  ${styles};
`;

const ExternalLink = styled.a.withConfig(config)`
  ${styles};
`;

const StyledLinkGatsby = styled(LinkGatsby).withConfig(config)`
  ${styles};
`;

interface ButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  to?: never;
}

interface AnchorProps extends AnchorHTMLAttributes<HTMLAnchorElement> {
  to: string;
}

type LinkProps = ButtonProps | AnchorProps;

function Link(props: LinkProps) {
  function handleClick(e: MouseEvent<HTMLAnchorElement>) {
    if (!props.to || !props.to.endsWith('#chat')) {
      return;
    }
    e.preventDefault();
    openChat();
  }

  if (props.to === undefined) {
    return <ButtonLink {...props} />;
  }

  const isExternal = useMemo(() => {
    const externalLinkRegexp = new RegExp('^((tel|https?|mailto):|#).*$');
    return externalLinkRegexp.test(props.to) || props.target === '_blank';
  }, [props.to]);

  if (isExternal) {
    const { to, ...rest } = props;
    return <ExternalLink href={to} onClick={handleClick} {...rest} />;
  }

  return (
    <StyledLinkGatsby {...props} to={props.to === '/home' ? '/' : props.to} />
  );
}

export default Link;
